<template>
  <span class="notification-bell">
    <!-- 顶栏铃铛图标 + 红点 -->
    <el-badge :value="unreadDisplay" :hidden="unreadUrgent === 0" :max="99" class="notification-bell__badge">
      <i class="el-icon-message-solid notification-bell__icon" @click.stop="openDrawer" :title="bellTitle"></i>
    </el-badge>

    <!-- 消息中心抽屉 -->
    <el-drawer
      title="消息中心"
      :visible.sync="drawerVisible"
      direction="rtl"
      size="520px"
      :wrapper-closable="true"
      append-to-body
      :modal-append-to-body="true"
      custom-class="notification-drawer"
      @opened="onDrawerOpened">
      <div class="notification-center">
        <!-- 顶部筛选 Tab -->
        <div class="notification-center__tabs">
          <el-radio-group v-model="activeTab" size="small" @change="onTabChange">
            <el-radio-button label="unread">
              未读
              <span v-if="unreadTotal > 0" class="notification-center__count">{{ unreadTotal }}</span>
            </el-radio-button>
            <el-radio-button label="urgent">
              紧急
              <span v-if="unreadUrgent > 0" class="notification-center__count notification-center__count--danger">{{ unreadUrgent }}</span>
            </el-radio-button>
            <el-radio-button label="overdue">超时未处理</el-radio-button>
            <el-radio-button label="all">全部</el-radio-button>
          </el-radio-group>
          <el-button
            type="text"
            size="small"
            :disabled="unreadTotal === 0"
            @click="handleMarkAllAsRead">全部已读</el-button>
        </div>

        <!-- 列表 -->
        <div v-loading="loading" class="notification-center__list">
          <div
            v-for="item in list"
            :key="item.id"
            class="notification-item"
            :class="{
              'notification-item--unread': item.status === StatusEnum.Unread,
              'notification-item--overdue': item.isOverdue
            }"
            @click="handleItemClick(item)">
            <!-- 级别色条 -->
            <span class="notification-item__bar" :style="{ background: severityColor(item.severity) }"></span>

            <div class="notification-item__main">
              <div class="notification-item__head">
                <el-tag :type="severityTag(item.severity)" size="mini">{{ severityLabel(item.severity) }}</el-tag>
                <el-tag :type="statusTag(item.status)" size="mini" style="margin-left: 6px;">{{ statusLabel(item.status) }}</el-tag>
                <el-tag v-if="item.isOverdue" type="danger" size="mini" effect="dark" style="margin-left: 6px;">已超时</el-tag>
                <span class="notification-item__title">{{ item.title }}</span>
              </div>

              <div class="notification-item__body">{{ item.body }}</div>

              <div class="notification-item__meta">
                <span>{{ item.creationTime | dateFormat }}</span>
                <span v-if="item.dueTime" class="notification-item__sla">
                  SLA：{{ slaText(item) }}
                </span>
              </div>

              <div class="notification-item__actions" @click.stop>
                <el-button
                  v-if="item.status === StatusEnum.Unread"
                  type="text"
                  size="mini"
                  @click="handleMarkAsRead(item)">标记已读</el-button>
                <el-button
                  v-if="item.status !== StatusEnum.Resolved && item.status !== StatusEnum.Dismissed"
                  type="text"
                  size="mini"
                  @click="handleMarkAsResolved(item)">标记已处理</el-button>
                <el-button
                  v-if="item.status !== StatusEnum.Resolved && item.status !== StatusEnum.Dismissed"
                  type="text"
                  size="mini"
                  @click="handleDismiss(item)">忽略</el-button>
              </div>
            </div>
          </div>

          <el-empty
            v-if="!loading && list.length === 0"
            :image-size="60"
            description="暂无消息"
            class="notification-center__empty" />
        </div>

        <!-- 翻页 -->
        <div class="notification-center__pager" v-if="total > pageSize">
          <el-pagination
            small
            layout="prev, pager, next, total"
            :total="total"
            :page-size="pageSize"
            :current-page.sync="currentPage"
            @current-change="loadList" />
        </div>
      </div>
    </el-drawer>
  </span>
</template>

<script>
import {
  getMyNotifications,
  getNotificationUnreadCount,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  markNotificationAsResolved,
  dismissNotification,
  NotificationSeverity,
  NotificationStatus,
  SeverityMeta,
  StatusMeta
} from '@/services/notification.service'
import dayjs from 'dayjs'

const POLL_INTERVAL_MS = 30 * 1000

export default {
  name: 'NotificationBell',
  data() {
    return {
      drawerVisible: false,
      loading: false,

      activeTab: 'unread', // unread | urgent | overdue | all
      list: [],
      total: 0,
      currentPage: 1,
      pageSize: 10,

      unreadTotal: 0,
      unreadUrgent: 0,

      pollTimer: null,
      StatusEnum: NotificationStatus
    }
  },
  computed: {
    unreadDisplay() {
      // 红点显示紧急未读数；非紧急未读不打扰
      return this.unreadUrgent
    },
    bellTitle() {
      if (this.unreadUrgent > 0) {
        return `有 ${this.unreadUrgent} 条紧急消息未处理`
      }
      if (this.unreadTotal > 0) {
        return `有 ${this.unreadTotal} 条未读消息`
      }
      return '消息中心'
    }
  },
  mounted() {
    this.refreshUnreadCount()
    this.startPolling()
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    // ----- 轮询未读数 -----
    startPolling() {
      this.stopPolling()
      this.pollTimer = setInterval(() => {
        this.refreshUnreadCount()
      }, POLL_INTERVAL_MS)
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    async refreshUnreadCount() {
      try {
        const res = await getNotificationUnreadCount()
        this.unreadTotal = res?.total ?? 0
        this.unreadUrgent = res?.urgent ?? 0
      } catch (e) {
        // 静默失败，避免顶栏报错刷屏
      }
    },

    // ----- 抽屉与列表 -----
    openDrawer() {
      this.drawerVisible = true
      this.currentPage = 1
      this.loadList()
    },
    onDrawerOpened() {
      this.refreshUnreadCount()
    },
    onTabChange() {
      this.currentPage = 1
      this.loadList()
    },
    buildListParams() {
      const params = {
        skipCount: (this.currentPage - 1) * this.pageSize,
        maxResultCount: this.pageSize,
        sorting: 'CreationTime desc'
      }
      if (this.activeTab === 'unread') {
        params.statuses = [NotificationStatus.Unread]
      } else if (this.activeTab === 'urgent') {
        params.statuses = [NotificationStatus.Unread, NotificationStatus.Read]
        params.severities = [NotificationSeverity.Critical, NotificationSeverity.Major]
      } else if (this.activeTab === 'overdue') {
        params.overdueOnly = true
      }
      return params
    },
    async loadList() {
      this.loading = true
      try {
        const params = this.buildListParams()
        const res = await getMyNotifications(params)
        this.list = (res?.items || []).map(this.normalize)
        this.total = res?.totalCount || 0
      } catch (e) {
        this.list = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },
    normalize(item) {
      let bizData = null
      if (item.bizDataJson) {
        try { bizData = JSON.parse(item.bizDataJson) } catch (e) { bizData = null }
      }
      return { ...item, bizData }
    },

    // ----- 操作 -----
    async handleMarkAsRead(item) {
      try {
        await markNotificationAsRead(item.id)
        item.status = NotificationStatus.Read
        item.readTime = new Date().toISOString()
        this.refreshUnreadCount()
      } catch (e) {
        this.$message.error('标记已读失败')
      }
    },
    async handleMarkAllAsRead() {
      try {
        await markAllNotificationsAsRead()
        this.$message.success('已全部标记已读')
        this.loadList()
        this.refreshUnreadCount()
      } catch (e) {
        this.$message.error('操作失败')
      }
    },
    async handleMarkAsResolved(item) {
      try {
        await markNotificationAsResolved(item.id)
        this.$message.success('已标记已处理')
        this.loadList()
        this.refreshUnreadCount()
      } catch (e) {
        this.$message.error('操作失败')
      }
    },
    async handleDismiss(item) {
      try {
        await dismissNotification(item.id)
        this.$message.success('已忽略')
        this.loadList()
        this.refreshUnreadCount()
      } catch (e) {
        this.$message.error('操作失败')
      }
    },

    // ----- 点击跳转 -----
    handleItemClick(item) {
      // 自动标已读
      if (item.status === NotificationStatus.Unread) {
        this.handleMarkAsRead(item)
      }
      // 按 BizType 路由跳转。本期只接入了 InsuranceUnilateral；其他业务接入时在此扩展。
      const route = this.resolveRoute(item)
      if (route) {
        this.drawerVisible = false
        this.$router.push(route).catch(() => {})
      }
    },
    resolveRoute(item) {
      switch (item.bizType) {
        case 'InsuranceUnilateral': {
          // 跳到对账明细页并预填筛选条件（页面侧需读取 query 参数）
          const mdtrtId = item.bizData?.mdtrtId
          return {
            path: '/index/his-reconciliation-detail',
            query: mdtrtId ? { mdtrtId, fromAlert: 1 } : { fromAlert: 1 }
          }
        }
        default:
          return null
      }
    },

    // ----- 展示 helper -----
    severityColor(s) { return SeverityMeta[s]?.color || '#909399' },
    severityLabel(s) { return SeverityMeta[s]?.label || '-' },
    severityTag(s) { return SeverityMeta[s]?.tagType || '' },
    statusLabel(s) { return StatusMeta[s]?.label || '-' },
    statusTag(s) { return StatusMeta[s]?.tagType || '' },
    slaText(item) {
      if (!item.dueTime) return '-'
      const due = dayjs(item.dueTime)
      const now = dayjs()
      const minutes = due.diff(now, 'minute')
      if (minutes >= 0) {
        if (minutes < 60) return `剩 ${minutes} 分钟`
        const hours = due.diff(now, 'hour')
        return `剩 ${hours} 小时`
      }
      const overMin = -minutes
      if (overMin < 60) return `已超时 ${overMin} 分钟`
      const overHour = Math.floor(overMin / 60)
      return `已超时 ${overHour} 小时`
    }
  }
}
</script>

<style scoped>
.notification-bell {
  display: inline-flex;
  align-items: center;
}

.notification-bell__badge {
  margin-right: 8px;
}

.notification-bell__icon {
  font-size: 22px;
  color: #606266;
  cursor: pointer;
  transition: color 0.2s;
}

.notification-bell__icon:hover {
  color: #409EFF;
}

.notification-center {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 0 16px 12px;
}

.notification-center__tabs {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #ebeef5;
  flex-shrink: 0;
}

.notification-center__count {
  margin-left: 4px;
  padding: 0 6px;
  background: #909399;
  color: #fff;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 600;
}

.notification-center__count--danger {
  background: #f56c6c;
}

.notification-center__list {
  flex: 1;
  overflow-y: auto;
  padding-top: 12px;
}

.notification-center__empty {
  padding: 30px 0;
}

.notification-center__pager {
  flex-shrink: 0;
  padding-top: 12px;
  text-align: center;
  border-top: 1px solid #ebeef5;
}

.notification-item {
  display: flex;
  padding: 10px 0;
  border-bottom: 1px solid #f2f6fc;
  cursor: pointer;
  transition: background 0.15s;
}

.notification-item:hover {
  background: #fafcff;
}

.notification-item--unread {
  background: #f0f9ff;
}

.notification-item--overdue {
  border-left: 0;
}

.notification-item__bar {
  width: 3px;
  margin-right: 10px;
  border-radius: 2px;
  flex-shrink: 0;
}

.notification-item__main {
  flex: 1;
  min-width: 0;
}

.notification-item__head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 0;
}

.notification-item__title {
  margin-left: 8px;
  font-weight: 500;
  color: #303133;
  word-break: break-all;
}

.notification-item__body {
  margin: 6px 0 4px;
  font-size: 12px;
  color: #606266;
  line-height: 1.6;
  word-break: break-all;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.notification-item__meta {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #909399;
}

.notification-item__sla {
  font-weight: 500;
}

.notification-item__actions {
  margin-top: 4px;
}

.notification-item__actions .el-button + .el-button {
  margin-left: 8px;
}
</style>

<!-- 非 scoped：drawer 用 append-to-body 后 DOM 不在本组件作用域里，scoped 选不到 -->
<style>
.notification-drawer .el-drawer__header {
  margin-bottom: 0 !important;
  padding: 16px 20px !important;
  border-bottom: 1px solid #ebeef5;
}
.notification-drawer .el-drawer__body {
  padding: 0 !important;
}
</style>
